import React, { useState, useEffect, useRef } from 'react';
import {
    Search, Plus, X, ChevronUp, ChevronDown,
    Edit2, Trash2, Filter, RefreshCw, Package,
    AlertCircle, Upload, Download, ToggleLeft, ToggleRight
} from 'lucide-react';
import API_BASE_URL from '../config';
import Swal from 'sweetalert2';

const ITEMS_API = `${API_BASE_URL}api/Marketing_items`;

const emptyForm = {
    id: 0,
    itemCode: '',
    description: '',
    qty: 0,
    availableQty: 0,
    unit: '',
    category: '',
    remarks: '',
    isActive: 1,
};

const CSV_HEADERS = [
    'id', 'itemCode', 'description', 'qty', 'availableQty',
    'unit', 'category', 'remarks', 'isActive'
];

const counter = (done, total, color) =>
    `<div style="text-align:center"><div style="font-size:48px;font-weight:bold;color:${color};margin:20px 0">${done}/${total}</div><div style="color:#64748b">Processing...</div></div>`;

const Marketing_Itemsmaintenance = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState(emptyForm);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterField, setFilterField] = useState('all');
    const [sortField, setSortField] = useState('id');
    const [sortDirection, setSortDirection] = useState('asc');
    const [currentPage, setCurrentPage] = useState(1);
    const [importData, setImportData] = useState([]);
    const [duplicates, setDuplicates] = useState([]);
    const fileInputRef = useRef(null);
    const itemsPerPage = 10;

    // ── Fetch ──────────────────────────────────────────────────────────────────
    const fetchItems = async () => {
        setLoading(true);
        try {
            const res = await fetch(ITEMS_API);
            if (res.ok) setItems(await res.json());
        } catch (err) { console.error('Fetch error:', err); }
        setLoading(false);
    };

    useEffect(() => { fetchItems(); }, []);

    // ── Create ─────────────────────────────────────────────────────────────────
    const createItem = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const res = await fetch(ITEMS_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', accept: 'text/plain' },
                body: JSON.stringify({ ...formData, qty: Number(formData.qty), availableQty: Number(formData.availableQty), isActive: Number(formData.isActive) }),
            });
            if (res.status === 409) {
                const err = await res.json();
                Swal.fire({ icon: 'warning', title: 'Duplicate Item Code', text: err.message, confirmButtonColor: '#185FA5' });
            } else if (res.ok) {
                closeModal(); fetchItems();
                Swal.fire({ icon: 'success', title: 'Created!', timer: 1500, showConfirmButton: false });
            }
        } catch (err) { console.error(err); }
        setLoading(false);
    };

    // ── Update ─────────────────────────────────────────────────────────────────
    const updateItem = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const res = await fetch(`${ITEMS_API}/${editingItem.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', accept: 'text/plain' },
                body: JSON.stringify({ ...formData, qty: Number(formData.qty), availableQty: Number(formData.availableQty), isActive: Number(formData.isActive) }),
            });
            if (res.status === 409) {
                const err = await res.json();
                Swal.fire({ icon: 'warning', title: 'Duplicate Item Code', text: err.message, confirmButtonColor: '#185FA5' });
            } else if (res.ok || res.status === 204) {
                closeModal(); fetchItems();
                Swal.fire({ icon: 'success', title: 'Updated!', timer: 1500, showConfirmButton: false });
            }
        } catch (err) { console.error(err); }
        setLoading(false);
    };

    // ── Delete single ──────────────────────────────────────────────────────────
    const deleteItem = async (id) => {
        const result = await Swal.fire({
            title: 'Delete Item?', text: 'This action cannot be undone!', icon: 'warning',
            showCancelButton: true, confirmButtonColor: '#E24B4A', cancelButtonColor: '#64748b',
            confirmButtonText: 'Yes, delete it!',
        });
        if (!result.isConfirmed) return;
        setLoading(true);
        try {
            const res = await fetch(`${ITEMS_API}/${id}`, { method: 'DELETE' });
            if (res.ok || res.status === 204) {
                setItems(prev => prev.filter(i => i.id !== id));
                Swal.fire({ icon: 'success', title: 'Deleted!', timer: 1200, showConfirmButton: false });
            }
        } catch (err) { console.error(err); }
        setLoading(false);
    };

    // ── Delete all ─────────────────────────────────────────────────────────────
    const deleteAllItems = async () => {
        if (items.length === 0) {
            return Swal.fire({ icon: 'info', title: 'No Items', text: 'Nothing to delete!', confirmButtonColor: '#185FA5' });
        }
        const first = await Swal.fire({
            title: 'Delete All Items?',
            html: `You are about to delete <strong>${items.length}</strong> items.<br/>This cannot be undone!`,
            icon: 'warning', showCancelButton: true,
            confirmButtonColor: '#E24B4A', cancelButtonColor: '#64748b', confirmButtonText: 'Yes, delete all!',
        });
        if (!first.isConfirmed) return;
        const second = await Swal.fire({
            title: 'Final Warning!', text: 'Permanently delete ALL items?', icon: 'error',
            showCancelButton: true, confirmButtonColor: '#E24B4A', cancelButtonColor: '#64748b', confirmButtonText: 'DELETE ALL',
        });
        if (!second.isConfirmed) return;

        setLoading(true);
        const total = items.length; let done = 0;
        Swal.fire({
            title: 'Deleting Items',
            html: counter(done, total, '#E24B4A'),
            allowOutsideClick: false, didOpen: () => Swal.showLoading(),
        });
        let success = 0, fail = 0;
        for (const item of items) {
            try {
                const res = await fetch(`${ITEMS_API}/${item.id}`, { method: 'DELETE' });
                res.ok || res.status === 204 ? success++ : fail++;
            } catch { fail++; }
            done++;
            Swal.update({ html: counter(done, total, '#E24B4A') });
        }
        await fetchItems();
        Swal.fire({
            icon: success === total ? 'success' : 'warning', title: 'Done',
            html: `✅ Deleted: <strong>${success}</strong>${fail > 0 ? `<br/>❌ Failed: <strong>${fail}</strong>` : ''}`,
            confirmButtonColor: '#185FA5',
        });
        setLoading(false);
    };

    // ── Export CSV ─────────────────────────────────────────────────────────────
    const handleExport = () => {
        if (items.length === 0) return Swal.fire({ icon: 'info', title: 'No Data', text: 'No items to export!', confirmButtonColor: '#185FA5' });
        const csvContent = [
            CSV_HEADERS.join(','),
            ...items.map(i => CSV_HEADERS.map(h => `"${i[h] ?? ''}"`).join(','))
        ].join('\n');
        const link = document.createElement('a');
        link.href = URL.createObjectURL(new Blob([csvContent], { type: 'text/csv;charset=utf-8;' }));
        link.download = `marketing_items_export_${Date.now()}.csv`;
        link.click();
    };

    // ── Import: File Select ────────────────────────────────────────────────────
    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const isExcel = file.name.endsWith('.xlsx') || file.name.endsWith('.xls');

        if (isExcel) {
            const processExcel = () => {
                const reader = new FileReader();
                reader.onload = (ev) => {
                    try {
                        const workbook = window.XLSX.read(ev.target.result, { type: 'array' });
                        const sheet = workbook.Sheets[workbook.SheetNames[0]];

                        // Read as array of arrays — position-based, ignore blank headers
                        const rawRows = window.XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
                        if (rawRows.length < 2) return Swal.fire({ icon: 'error', title: 'Empty File', text: 'No data found in Excel file.' });

                        // Find header row — first row containing 'itemcode'
                        const headerRowIdx = rawRows.findIndex(row =>
                            row.some(cell => String(cell).toLowerCase().trim() === 'itemcode')
                        );
                        if (headerRowIdx === -1) return Swal.fire({ icon: 'error', title: 'Invalid Excel', text: 'Cannot find "itemCode" column header.' });

                        const headers = rawRows[headerRowIdx].map(h => String(h).toLowerCase().trim());

                        if (headers.indexOf('itemcode') === -1)
                            return Swal.fire({ icon: 'error', title: 'Invalid Excel', text: 'Missing column: itemCode' });

                        const dataRows = rawRows.slice(headerRowIdx + 1).filter(row => row.some(cell => cell !== ''));

                        // Helper para i-clean ang numbers na may comma formatting
                        const toNum = (val) => {
                            if (val === '' || val === null || val === undefined) return 0;
                            if (typeof val === 'number') return val;
                            return Number(String(val).replace(/,/g, '').trim()) || 0;
                        };

                        const toStr = (val) => String(val ?? '').trim();

                        const normalizedParsed = dataRows.map(row => ({
                            itemCode: toStr(row[headers.indexOf('itemcode')]),
                            description: toStr(row[headers.indexOf('description')]),
                            qty: toNum(row[headers.indexOf('qty')]),
                            availableQty: toNum(row[headers.indexOf('availableqty')]),
                            unit: toStr(row[headers.indexOf('unit')]),
                            category: toStr(row[headers.indexOf('category')]),
                            remarks: toStr(row[headers.indexOf('remarks')]),
                            isActive: (() => {
                                const val = row[headers.indexOf('isactive')];
                                const str = String(val ?? '').toLowerCase().trim();
                                if (str === 'inactive' || str === '0' || val === 0) return 0;
                                return 1;
                            })(),
                        })).filter(row => row.itemCode !== '');

                        if (!normalizedParsed.length) return Swal.fire({ icon: 'error', title: 'Empty File', text: 'No valid data rows found.' });

                        checkDuplicates(normalizedParsed);
                        setShowImportModal(true);
                    } catch (err) {
                        console.error(err);
                        Swal.fire({ icon: 'error', title: 'Parse Error', text: 'Failed to read Excel file. Make sure it is a valid .xlsx or .xls file.' });
                    }
                };
                reader.readAsArrayBuffer(file);
            };

            if (window.XLSX) {
                processExcel();
            } else {
                const script = document.createElement('script');
                script.src = 'https://cdn.jsdelivr.net/npm/xlsx@0.18.5/dist/xlsx.full.min.js';
                script.onload = processExcel;
                script.onerror = () => Swal.fire({ icon: 'error', title: 'Load Failed', text: 'Could not load Excel parser. Check your connection.' });
                document.head.appendChild(script);
            }

        } else {
            const reader = new FileReader();
            reader.onload = (ev) => {
                try {
                    const lines = ev.target.result.split('\n').filter(l => l.trim());
                    if (lines.length < 2) return Swal.fire({ icon: 'error', title: 'Invalid File', text: 'CSV must have header + at least 1 row.' });
                    const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
                    const requiredHeaders = ['itemCode', 'description'];
                    const missing = requiredHeaders.filter(h => !headers.includes(h));
                    if (missing.length) return Swal.fire({ icon: 'error', title: 'Invalid CSV', text: `Missing columns: ${missing.join(', ')}` });
                    const parsed = lines.slice(1).map(line => {
                        const vals = line.split(',').map(v => v.trim().replace(/"/g, ''));
                        const obj = {};
                        headers.forEach((h, i) => { obj[h] = vals[i] ?? ''; });
                        return obj;
                    });
                    checkDuplicates(parsed);
                    setShowImportModal(true);
                } catch (err) {
                    console.error(err);
                    Swal.fire({ icon: 'error', title: 'Parse Error', text: 'Failed to read CSV file.' });
                }
            };
            reader.readAsText(file);
        }

        if (fileInputRef.current) fileInputRef.current.value = '';
    };
    // ── Import: Check Duplicates ───────────────────────────────────────────────
    const checkDuplicates = (rows) => {
        const dupes = [], nonDupes = [];
        rows.forEach(row => {
            const existing = items.find(i => i.itemCode?.toLowerCase() === row.itemCode?.toLowerCase());
            if (existing) {
                dupes.push({ ...row, existingId: existing.id, existing, action: 'skip' });
            } else {
                nonDupes.push(row);
            }
        });
        setImportData(nonDupes);
        setDuplicates(dupes);
    };

    const handleDuplicateAction = (index, action) => {
        setDuplicates(prev => { const u = [...prev]; u[index].action = action; return u; });
    };

    // ── Import: Process ────────────────────────────────────────────────────────
    const processImport = async () => {
        const toUpdate = duplicates.filter(d => d.action === 'update');
        const total = importData.length + toUpdate.length;
        let done = 0;
        setLoading(true);

        Swal.fire({
            title: 'Importing Items',
            html: counter(done, total, '#185FA5'),
            allowOutsideClick: false, didOpen: () => Swal.showLoading(),
        });

        try {
            for (const row of importData) {
                await fetch(ITEMS_API, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', accept: 'text/plain' },
                    body: JSON.stringify({
                        id: 0,
                        itemCode: row.itemCode || '',
                        description: row.description || '',
                        qty: Number(row.qty) || 0,
                        availableQty: Number(row.availableQty) || 0,
                        unit: row.unit || '',
                        category: row.category || '',
                        remarks: row.remarks || '',
                        isActive: Number(row.isActive) || 1,
                    }),
                });
                done++;
                Swal.update({ html: counter(done, total, '#185FA5') });
            }

            for (const dup of toUpdate) {
                await fetch(`${ITEMS_API}/${dup.existingId}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json', accept: 'text/plain' },
                    body: JSON.stringify({
                        id: dup.existingId,
                        itemCode: dup.itemCode || '',
                        description: dup.description || '',
                        qty: Number(dup.qty) || 0,
                        availableQty: Number(dup.availableQty) || 0,
                        unit: dup.unit || '',
                        category: dup.category || '',
                        remarks: dup.remarks || '',
                        isActive: Number(dup.isActive) || 1,
                    }),
                });
                done++;
                Swal.update({ html: counter(done, total, '#185FA5') });
            }

            await fetchItems();
            closeImportModal();
            Swal.fire({
                icon: 'success', title: 'Import Complete!',
                html: `✅ Created: <strong>${importData.length}</strong><br/>🔄 Updated: <strong>${toUpdate.length}</strong><br/>⏭ Skipped: <strong>${duplicates.filter(d => d.action === 'skip').length}</strong>`,
                confirmButtonColor: '#185FA5',
            });
        } catch (err) {
            console.error(err);
            Swal.fire({ icon: 'error', title: 'Import Failed', text: 'An error occurred.', confirmButtonColor: '#E24B4A' });
        }
        setLoading(false);
    };

    const closeImportModal = () => {
        setShowImportModal(false);
        setImportData([]);
        setDuplicates([]);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    // ── Modal helpers ──────────────────────────────────────────────────────────
    const openCreateModal = () => { setEditingItem(null); setFormData(emptyForm); setShowModal(true); };
    const openEditModal = (item) => { setEditingItem(item); setFormData({ ...item }); setShowModal(true); };
    const closeModal = () => { setShowModal(false); setEditingItem(null); setFormData(emptyForm); };
    const handleInputChange = (e) => {
        const { name, value, type } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'number' ? value : value }));
    };

    // ── Filter / Sort / Paginate ───────────────────────────────────────────────
    const filtered = items.filter(i => {
        const q = searchTerm.toLowerCase();
        if (!q) return true;
        if (filterField === 'all') return (
            i.itemCode?.toLowerCase().includes(q) ||
            i.description?.toLowerCase().includes(q) ||
            i.unit?.toLowerCase().includes(q) ||
            i.category?.toLowerCase().includes(q) ||
            i.remarks?.toLowerCase().includes(q)
        );
        return String(i[filterField])?.toLowerCase().includes(q);
    });

    const sorted = [...filtered].sort((a, b) => {
        let av = a[sortField] ?? '', bv = b[sortField] ?? '';
        if (typeof av === 'string') av = av.toLowerCase();
        if (typeof bv === 'string') bv = bv.toLowerCase();
        return sortDirection === 'asc' ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
    });

    const totalPages = Math.ceil(sorted.length / itemsPerPage);
    const paginated = sorted.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    const handleSort = (field) => {
        if (sortField === field) setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
        else { setSortField(field); setSortDirection('asc'); }
    };

    const SortIcon = ({ field }) => {
        if (sortField !== field) return <ChevronUp className="opacity-30" size={14} />;
        return sortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
    };

    const getStockBadge = (avail) => {
        if (avail <= 0) return <span className="badge-out">Out of stock</span>;
        if (avail < 10) return <span className="badge-low">Low stock</span>;
        return <span className="badge-in">In stock</span>;
    };

    // ── Render ─────────────────────────────────────────────────────────────────
    return (
        <div style={{
            minHeight: '100vh',
            background: '#f1f5f9',
            padding: '2rem 1.5rem',
            fontFamily: '"Inter", -apple-system, sans-serif',
        }}>
            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        * { margin: 0; padding: 0; box-sizing: border-box; }

        .glass-card { background: #ffffff; border: 1px solid #e2e8f0; border-radius: 14px; box-shadow: 0 1px 4px rgba(0,0,0,0.06); }

        .btn { height: 38px; padding: 0 16px; border-radius: 8px; border: none; font-weight: 500; font-size: 13px; cursor: pointer; transition: all .2s; display: inline-flex; align-items: center; gap: 6px; font-family: 'Inter', sans-serif; white-space: nowrap; }
        .btn:hover  { opacity: .88; transform: translateY(-1px); }
        .btn:active { transform: scale(.97); }
        .btn:disabled { opacity: .45; cursor: not-allowed; transform: none; }
        .btn-primary  { background: #185FA5; color: #fff; }
        .btn-success  { background: #1D9E75; color: #fff; }
        .btn-warning  { background: #BA7517; color: #fff; }
        .btn-danger   { background: #E24B4A; color: #fff; }
        .btn-ghost    { background: #fff; color: #475569; border: 1px solid #e2e8f0; }
        .btn-ghost:hover { background: #f8fafc; }

        .input-field { width: 100%; height: 38px; padding: 0 12px; background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; color: #1e293b; font-size: 14px; font-family: 'Inter', sans-serif; transition: border-color .2s, box-shadow .2s; }
        .input-field:focus { outline: none; border-color: #185FA5; box-shadow: 0 0 0 3px rgba(24,95,165,.1); }
        .input-field::placeholder { color: #94a3b8; }

        .select-field { height: 38px; padding: 0 32px 0 36px; background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; color: #1e293b; font-size: 14px; font-family: 'Inter', sans-serif; cursor: pointer; appearance: none; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 12 12'%3E%3Cpath fill='%2394a3b8' d='M6 9L1 4h10z'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 10px center; background-color: #fff; transition: border-color .2s, box-shadow .2s; }
        .select-field:focus { outline: none; border-color: #185FA5; box-shadow: 0 0 0 3px rgba(24,95,165,.1); }

        .icon-wrap { position: relative; }
        .icon-wrap .icon-left { position: absolute; left: 11px; top: 50%; transform: translateY(-50%); color: #94a3b8; pointer-events: none; display: flex; }

        .controls-grid { display: grid; grid-template-columns: 1fr auto auto; gap: 10px; align-items: center; }
        .controls-left { display: grid; grid-template-columns: 240px auto; gap: 8px; align-items: center; }
        .controls-right { display: flex; gap: 6px; align-items: center; }
        .btn-divider { width: 1px; height: 24px; background: #e2e8f0; margin: 0 2px; }

        .stat-badge { display: inline-flex; align-items: center; gap: 6px; padding: 4px 12px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 99px; font-size: 12px; color: #64748b; font-weight: 500; }
        .stat-dot { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; }

        .table-container { overflow-x: auto; border-radius: 12px; }
        table { width: 100%; border-collapse: collapse; }
        th { background: #f8fafc; padding: .875rem 1rem; text-align: left; font-weight: 600; color: #64748b; font-size: .78rem; text-transform: uppercase; letter-spacing: .06em; cursor: pointer; user-select: none; border-bottom: 1px solid #e2e8f0; white-space: nowrap; transition: background .15s; }
        th:hover { background: #f1f5f9; }
        td { padding: .875rem 1rem; border-bottom: 1px solid #f1f5f9; color: #334155; font-size: .875rem; }
        tr:hover td { background: #fafbfd; }
        tr:last-child td { border-bottom: none; }
        .th-inner { display: flex; align-items: center; gap: 4px; }

        .modal-overlay { position: fixed; inset: 0; background: rgba(15,23,42,.45); backdrop-filter: blur(6px); display: flex; align-items: center; justify-content: center; z-index: 1000; animation: fadeIn .2s ease; }
        @keyframes fadeIn { from{opacity:0} to{opacity:1} }
        .modal-content { animation: slideUp .25s cubic-bezier(.4,0,.2,1); width: min(560px, 92vw); max-height: 92vh; overflow-y: auto; }
        .modal-content-lg { animation: slideUp .25s cubic-bezier(.4,0,.2,1); width: min(860px, 95vw); max-height: 92vh; overflow-y: auto; }
        @keyframes slideUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }

        .form-label { display: block; margin-bottom: 6px; color: #475569; font-weight: 500; font-size: .85rem; }
        .form-group { display: flex; flex-direction: column; }

        .pagination { display: flex; gap: 4px; align-items: center; justify-content: center; flex-wrap: wrap; }
        .page-btn { height: 34px; min-width: 34px; padding: 0 10px; background: #fff; border: 1px solid #e2e8f0; border-radius: 7px; color: #475569; cursor: pointer; transition: all .15s; font-size: .875rem; font-family: 'Inter', sans-serif; }
        .page-btn:hover:not(:disabled) { background: #f1f5f9; border-color: #185FA5; }
        .page-btn.active { background: #185FA5; border-color: #185FA5; color: #fff; font-weight: 600; }
        .page-btn:disabled { opacity: .4; cursor: not-allowed; }
        .page-ellipsis { color: #94a3b8; padding: 0 4px; line-height: 34px; }

        .spinner { border: 2px solid #e2e8f0; border-top: 2px solid #185FA5; border-radius: 50%; animation: spin .7s linear infinite; flex-shrink: 0; }
        @keyframes spin { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }

        .icon-btn { width: 32px; height: 32px; background: transparent; border: none; cursor: pointer; border-radius: 7px; transition: all .15s; display: inline-flex; align-items: center; justify-content: center; color: #94a3b8; }
        .icon-btn.edit:hover  { background: #eff6ff; color: #185FA5; }
        .icon-btn.delete:hover { background: #fef2f2; color: #E24B4A; }

        .dup-card { background: #fffbeb; border: 1px solid #fde68a; padding: 1rem; border-radius: 10px; }
        .act-btn { height: 30px; padding: 0 12px; border-radius: 6px; border: none; font-size: .8rem; cursor: pointer; transition: all .15s; font-family: 'Inter', sans-serif; font-weight: 500; }
        .act-skip   { background: #f1f5f9; color: #64748b; }
        .act-skip.on   { background: #94a3b8; color: #fff; }
        .act-update { background: #dbeafe; color: #1e40af; }
        .act-update.on { background: #185FA5; color: #fff; }

        .badge-in  { padding: 3px 10px; border-radius: 99px; font-size: .78rem; font-weight: 600; background: #d1fae5; color: #065f46; }
        .badge-low { padding: 3px 10px; border-radius: 99px; font-size: .78rem; font-weight: 600; background: #fef9c3; color: #854d0e; }
        .badge-out { padding: 3px 10px; border-radius: 99px; font-size: .78rem; font-weight: 600; background: #fee2e2; color: #991b1b; }
        .badge-active   { padding: 3px 10px; border-radius: 99px; font-size: .78rem; font-weight: 600; background: #d1fae5; color: #065f46; }
        .badge-inactive { padding: 3px 10px; border-radius: 99px; font-size: .78rem; font-weight: 600; background: #f1f5f9; color: #94a3b8; }

        .toggle-active   { display: inline-flex; align-items: center; gap: 5px; padding: 3px 10px; border-radius: 99px; font-size: .78rem; font-weight: 600; background: #d1fae5; color: #065f46; cursor: pointer; border: none; transition: all .15s; }
        .toggle-inactive { display: inline-flex; align-items: center; gap: 5px; padding: 3px 10px; border-radius: 99px; font-size: .78rem; font-weight: 600; background: #f1f5f9; color: #94a3b8; cursor: pointer; border: none; transition: all .15s; }

        input[type="file"] { display: none; }

        textarea.input-field { height: auto; padding: 8px 12px; resize: vertical; }

        @media (max-width: 768px) {
          .controls-grid { grid-template-columns: 1fr; }
          .controls-left { grid-template-columns: 1fr; }
          .controls-right { flex-wrap: wrap; }
        }
      `}</style>

            <div style={{ maxWidth: '1700px', margin: '0 auto' }}>

                {/* ── Header ── */}
                <div style={{ marginBottom: '1.75rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <div style={{
                        width: 52, height: 52, flexShrink: 0,
                        background: 'linear-gradient(135deg,#185FA5,#0C447C)',
                        borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: '0 4px 12px rgba(24,95,165,.25)',
                    }}>
                        <Package size={26} color="#fff" />
                    </div>
                    <div>
                        <h1 style={{ fontSize: '1.75rem', fontWeight: 700, color: '#0f172a', letterSpacing: '-0.02em', lineHeight: 1.2 }}>
                            Items Maintenance
                        </h1>
                        <p style={{ color: '#64748b', fontSize: '.9rem', marginTop: 2 }}>
                            Manage marketing inventory items and stock levels
                        </p>
                    </div>
                </div>

                {/* ── Controls Card ── */}
                <div className="glass-card" style={{ padding: '1.25rem 1.5rem', marginBottom: '1.25rem' }}>
                    <div className="controls-grid">
                        <div className="controls-left">
                            <div className="icon-wrap">
                                <span className="icon-left"><Search size={15} /></span>
                                <input
                                    type="text"
                                    placeholder="Search items..."
                                    value={searchTerm}
                                    onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                                    className="input-field"
                                    style={{ paddingLeft: 36 }}
                                />
                            </div>
                            <div className="icon-wrap">
                                <span className="icon-left"><Filter size={14} /></span>
                                <select value={filterField} onChange={e => setFilterField(e.target.value)} className="select-field">
                                    <option value="all">All Fields</option>
                                    <option value="itemCode">Item Code</option>
                                    <option value="description">Description</option>
                                    <option value="unit">Unit</option>
                                    <option value="category">Category</option>
                                    <option value="remarks">Remarks</option>
                                </select>
                            </div>
                        </div>

                        <button onClick={fetchItems} className="btn btn-ghost" disabled={loading}>
                            <RefreshCw size={14} style={{ animation: loading ? 'spin .7s linear infinite' : 'none' }} />
                            Refresh
                        </button>

                        <div className="controls-right">
                            <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" onChange={handleFileSelect} />
                            <button onClick={() => fileInputRef.current?.click()} className="btn btn-success">
                                <Upload size={14} /> Import
                            </button>
                            <button onClick={handleExport} className="btn btn-warning">
                                <Download size={14} /> Export
                            </button>
                            <div className="btn-divider" />
                            <button onClick={deleteAllItems} className="btn btn-danger" disabled={loading || items.length === 0}>
                                <Trash2 size={14} /> Delete All
                            </button>
                            <button onClick={openCreateModal} className="btn btn-primary">
                                <Plus size={14} /> Add New
                            </button>
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: 8, marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #f1f5f9', flexWrap: 'wrap' }}>
                        <span className="stat-badge"><span className="stat-dot" style={{ background: '#185FA5' }} />Total: {items.length}</span>
                        <span className="stat-badge"><span className="stat-dot" style={{ background: '#1D9E75' }} />Filtered: {filtered.length}</span>
                        <span className="stat-badge"><span className="stat-dot" style={{ background: '#1D9E75' }} />Active: {items.filter(i => i.isActive === 1).length}</span>
                        <span className="stat-badge"><span className="stat-dot" style={{ background: '#BA7517' }} />Page {currentPage} of {totalPages || 1}</span>
                    </div>
                </div>

                {/* ── Table ── */}
                <div className="glass-card" style={{ marginBottom: '1.25rem' }}>
                    <div className="table-container">
                        {loading && paginated.length === 0 ? (
                            <div style={{ padding: '4rem', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
                                <div className="spinner" style={{ width: 28, height: 28 }} />
                                <p style={{ color: '#64748b', fontSize: '.9rem' }}>Loading items...</p>
                            </div>
                        ) : paginated.length === 0 ? (
                            <div style={{ padding: '4rem', textAlign: 'center' }}>
                                <p style={{ color: '#94a3b8', fontSize: '1rem' }}>
                                    {searchTerm ? 'No items match your search.' : 'No items yet. Click "Add New" to get started.'}
                                </p>
                            </div>
                        ) : (
                            <table>
                                <thead>
                                    <tr>
                                        {[
                                            { label: 'ID', field: 'id' },
                                            { label: 'Item Code', field: 'itemCode' },
                                            { label: 'Description', field: 'description' },
                                            { label: 'Qty', field: 'qty' },
                                            { label: 'Available Qty', field: 'availableQty' },
                                            { label: 'Unit', field: 'unit' },
                                            { label: 'Category', field: 'category' },
                                            { label: 'Stock Status', field: 'availableQty' },
                                            { label: 'Active', field: 'isActive' },
                                            { label: 'Remarks', field: 'remarks' },
                                        ].map(col => (
                                            <th key={col.label} onClick={() => handleSort(col.field)}>
                                                <div className="th-inner">{col.label} <SortIcon field={col.field} /></div>
                                            </th>
                                        ))}
                                        <th style={{ textAlign: 'center' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginated.map(item => (
                                        <tr key={item.id}>
                                            <td style={{ fontWeight: 600, color: '#185FA5', fontSize: '.8rem' }}>#{item.id}</td>
                                            <td style={{ fontWeight: 600, color: '#0f172a', fontFamily: 'monospace' }}>{item.itemCode}</td>
                                            <td style={{ maxWidth: 200 }}>{item.description || <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                                            <td style={{ fontWeight: 600, textAlign: 'right' }}>{Number(item.qty || 0).toLocaleString()}</td>
                                            <td style={{ fontWeight: 600, textAlign: 'right', color: item.availableQty > 0 ? '#1D9E75' : '#E24B4A' }}>
                                                {Number(item.availableQty || 0).toLocaleString()}
                                            </td>
                                            <td>{item.unit || <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                                            <td>{item.category || <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                                            <td>{getStockBadge(item.availableQty)}</td>
                                            <td>
                                                <span className={item.isActive === 1 ? 'badge-active' : 'badge-inactive'}>
                                                    {item.isActive === 1 ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td style={{ maxWidth: 160, color: '#64748b', fontSize: '.82rem' }}>
                                                {item.remarks || <span style={{ color: '#cbd5e1' }}>—</span>}
                                            </td>
                                            <td>
                                                <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                                                    <button onClick={() => openEditModal(item)} className="icon-btn edit" title="Edit">
                                                        <Edit2 size={15} />
                                                    </button>
                                                    <button onClick={() => deleteItem(item.id)} className="icon-btn delete" title="Delete">
                                                        <Trash2 size={15} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>

                {/* ── Pagination ── */}
                {totalPages > 1 && (
                    <div className="glass-card" style={{ padding: '1rem' }}>
                        <div className="pagination">
                            <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="page-btn">First</button>
                            <button onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1} className="page-btn">‹</button>
                            {[...Array(totalPages)].map((_, i) => {
                                const p = i + 1;
                                if (p === 1 || p === totalPages || (p >= currentPage - 1 && p <= currentPage + 1))
                                    return <button key={p} onClick={() => setCurrentPage(p)} className={`page-btn ${currentPage === p ? 'active' : ''}`}>{p}</button>;
                                if (p === currentPage - 2 || p === currentPage + 2)
                                    return <span key={p} className="page-ellipsis">···</span>;
                                return null;
                            })}
                            <button onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage === totalPages} className="page-btn">›</button>
                            <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="page-btn">Last</button>
                        </div>
                    </div>
                )}

                {/* ── Add / Edit Modal ── */}
                {showModal && (
                    <div className="modal-overlay" onClick={closeModal}>
                        <div className="modal-content glass-card" onClick={e => e.stopPropagation()}>
                            <div style={{ padding: '1.5rem 1.75rem', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#0f172a' }}>
                                    {editingItem ? 'Edit Item' : 'Add New Item'}
                                </h2>
                                <button onClick={closeModal} className="icon-btn" style={{ color: '#94a3b8' }}><X size={20} /></button>
                            </div>
                            <form onSubmit={editingItem ? updateItem : createItem}>
                                <div style={{ padding: '1.5rem 1.75rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                    <div className="form-group">
                                        <label className="form-label">Item Code <span style={{ color: '#E24B4A' }}>*</span></label>
                                        <input name="itemCode" type="text" value={formData.itemCode} onChange={handleInputChange} className="input-field" required placeholder="e.g. ITEM-001" />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Description</label>
                                        <input name="description" type="text" value={formData.description} onChange={handleInputChange} className="input-field" placeholder="Item description" />
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                        <div className="form-group">
                                            <label className="form-label">Quantity</label>
                                            <input name="qty" type="number" value={formData.qty} onChange={handleInputChange} className="input-field" placeholder="0" min="0" />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Available Qty</label>
                                            <input name="availableQty" type="number" value={formData.availableQty} onChange={handleInputChange} className="input-field" placeholder="0" min="0" />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Unit</label>
                                            <input name="unit" type="text" value={formData.unit} onChange={handleInputChange} className="input-field" placeholder="PCS, BOX, KG..." />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Category</label>
                                            <input name="category" type="text" value={formData.category} onChange={handleInputChange} className="input-field" placeholder="Category" />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Remarks</label>
                                        <textarea name="remarks" value={formData.remarks} onChange={handleInputChange} className="input-field" rows={3} placeholder="Optional remarks..." />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Status</label>
                                        <select name="isActive" value={formData.isActive} onChange={handleInputChange} className="input-field" style={{ height: 38 }}>
                                            <option value={1}>Active</option>
                                            <option value={0}>Inactive</option>
                                        </select>
                                    </div>
                                </div>
                                <div style={{ padding: '1rem 1.75rem', borderTop: '1px solid #f1f5f9', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                    <button type="button" onClick={closeModal} className="btn btn-ghost">Cancel</button>
                                    <button type="submit" className="btn btn-primary" disabled={loading}>
                                        {loading
                                            ? <><div className="spinner" style={{ width: 14, height: 14 }} />{editingItem ? 'Updating...' : 'Creating...'}</>
                                            : editingItem ? 'Update Item' : 'Create Item'
                                        }
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* ── Import Modal ── */}
                {showImportModal && (
                    <div className="modal-overlay" onClick={closeImportModal}>
                        <div className="modal-content-lg glass-card" onClick={e => e.stopPropagation()}>
                            <div style={{ padding: '1.5rem 1.75rem', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#0f172a' }}>Import Items</h2>
                                <button onClick={closeImportModal} className="icon-btn" style={{ color: '#94a3b8' }}><X size={20} /></button>
                            </div>

                            <div style={{ padding: '1.5rem 1.75rem' }}>
                                <div style={{ display: 'flex', gap: 8, marginBottom: '1.25rem', flexWrap: 'wrap' }}>
                                    <span className="stat-badge"><span className="stat-dot" style={{ background: '#1D9E75' }} />New: {importData.length}</span>
                                    <span className="stat-badge"><span className="stat-dot" style={{ background: '#BA7517' }} />Duplicates: {duplicates.length}</span>
                                </div>

                                {duplicates.length > 0 && (
                                    <div style={{ marginBottom: '1.25rem' }}>
                                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '1rem', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '10px', marginBottom: '1rem', color: '#92400e' }}>
                                            <AlertCircle size={18} style={{ flexShrink: 0, marginTop: 1 }} />
                                            <div>
                                                <strong style={{ fontSize: '.9rem' }}>Duplicate Item Codes found!</strong>
                                                <p style={{ fontSize: '.84rem', marginTop: 4, color: '#78350f' }}>
                                                    Choose — <strong>Skip</strong> to keep existing, or <strong>Update</strong> to overwrite.
                                                </p>
                                            </div>
                                        </div>
                                        <div style={{ maxHeight: '340px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                            {duplicates.map((dup, i) => (
                                                <div key={i} className="dup-card">
                                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '10px', fontSize: '.84rem' }}>
                                                        <div>
                                                            <div style={{ fontWeight: 600, color: '#92400e', marginBottom: 4 }}>Existing</div>
                                                            <div style={{ color: '#475569', display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                                <span><b>Code:</b> {dup.existing.itemCode}</span>
                                                                <span><b>Desc:</b> {dup.existing.description || '—'}</span>
                                                                <span><b>Qty:</b> {dup.existing.qty}</span>
                                                                <span><b>Avail:</b> {dup.existing.availableQty}</span>
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <div style={{ fontWeight: 600, color: '#1e40af', marginBottom: 4 }}>Import Data</div>
                                                            <div style={{ color: '#475569', display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                                <span><b>Code:</b> {dup.itemCode}</span>
                                                                <span><b>Desc:</b> {dup.description || '—'}</span>
                                                                <span><b>Qty:</b> {dup.qty}</span>
                                                                <span><b>Avail:</b> {dup.availableQty}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div style={{ display: 'flex', gap: 6 }}>
                                                        <button className={`act-btn act-skip ${dup.action === 'skip' ? 'on' : ''}`} onClick={() => handleDuplicateAction(i, 'skip')}>⏭ Skip</button>
                                                        <button className={`act-btn act-update ${dup.action === 'update' ? 'on' : ''}`} onClick={() => handleDuplicateAction(i, 'update')}>🔄 Update</button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {importData.length > 0 && (
                                    <div>
                                        <p style={{ fontSize: '.9rem', fontWeight: 600, color: '#0f172a', marginBottom: '10px' }}>
                                            New items to import ({importData.length})
                                        </p>
                                        <div style={{ maxHeight: '240px', overflowY: 'auto', background: '#f8fafc', padding: '10px', borderRadius: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                            {importData.slice(0, 10).map((item, i) => (
                                                <div key={i} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '10px 12px', fontSize: '.84rem' }}>
                                                    <div style={{ fontWeight: 600, color: '#185FA5', fontFamily: 'monospace' }}>{item.itemCode}</div>
                                                    <div style={{ color: '#475569', marginTop: 2 }}>{item.description || '—'}</div>
                                                    <div style={{ display: 'flex', gap: '12px', marginTop: 4, color: '#94a3b8', fontSize: '.78rem' }}>
                                                        <span>Qty: {item.qty || 0}</span>
                                                        <span>Avail: {item.availableQty || 0}</span>
                                                        <span>Unit: {item.unit || '—'}</span>
                                                        <span>Category: {item.category || '—'}</span>
                                                    </div>
                                                </div>
                                            ))}
                                            {importData.length > 10 && (
                                                <p style={{ textAlign: 'center', color: '#94a3b8', fontSize: '.84rem', padding: '6px 0' }}>
                                                    +{importData.length - 10} more records
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div style={{ padding: '1rem 1.75rem', borderTop: '1px solid #f1f5f9', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                <button onClick={closeImportModal} className="btn btn-ghost">Cancel</button>
                                <button
                                    onClick={processImport}
                                    className="btn btn-primary"
                                    disabled={loading || (importData.length === 0 && duplicates.every(d => d.action === 'skip'))}
                                >
                                    {loading
                                        ? <><div className="spinner" style={{ width: 14, height: 14 }} />Processing...</>
                                        : `Import ${importData.length + duplicates.filter(d => d.action === 'update').length} Items`
                                    }
                                </button>
                            </div>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
};

export default Marketing_Itemsmaintenance;